import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  TextField,
} from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import * as React from 'react';
import { Fragment, PropsWithChildren, useState } from 'react';
import { GroupSurveyConfigurationModal } from '@components/modals';
import { GroupView } from '@views';
import { DebugPanel } from '@components';
import { NoParamCallback } from 'node:fs';
import { AccountabilityPartnerPairingEditor } from '@components/Admin/AccountabilityPartnerPairingEditor/AccountabilityPartnerPairingEditor';
import { NotificationService, CommunicationMode } from '@app/api';
import { ChatStore } from '@app/api/ChatStore';
import { PledgeDialog } from './PledgeDialog';
import { useAppCookies } from '@app/providers/AppCookieProvider';
import { ReportCardDialog } from './ReportCardDialog';
import { EngagementReportDialog } from './EngagementReportDialog';

interface GroupActionCellProps extends PropsWithChildren {
  group: GroupView;
  updateSummary: NoParamCallback;
}

export const GroupActionCell: React.FC<GroupActionCellProps> = ({ updateSummary, group }) => {
  const [configureSurveyDialogOpen, setConfigureSurveyDialogOpen] = useState<boolean>(false);
  const [showGroupMessage, setShowGroupMessage] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const moreMenuOpen = Boolean(anchorEl);
  const [configurePledgeDialogOpen, setConfigurePledgeDialogOpen] = useState<boolean>(false);
  const [groupMessage, setGroupMessage] = useState<string>('');
  const cookies = useAppCookies();
  const [apSettingOpen, setAPSettingOpen] = useState<boolean>(false);
  const [reportCardDialogOpen, setReportCardDialogOpen] = useState<boolean>(false);
  const [engagementReportDialogOpen, setEngagementReportDialogOpen] = useState<boolean>(false);

  const handleCloseConfigureSurveyDialogModal = () => {
    setConfigureSurveyDialogOpen(false);
  };
  const handleSetPledge = () => {
    setAnchorEl(null);
    setConfigurePledgeDialogOpen(true);
  };
  const handleSendMessageMenuItemClick = () => {
    setAnchorEl(null);
    setShowGroupMessage(true);
  };
  const handleReportCard = () => {
    setAnchorEl(null);
    setReportCardDialogOpen(true);
  };
  const handleEngagementReport = () => {
    setAnchorEl(null);
    setEngagementReportDialogOpen(true);
  };
  const handleSendMessage = async () => {
    if (!group.channel) {
      console.error('No channel URL available for sending messages');
      return;
    }

    try {
      const chatStore = new ChatStore();
      await chatStore.PostAdminChatMessage(group.channel, groupMessage);

      // Send a push notification to the cohort
      await new NotificationService().sendNotification({
        groupId: Number(group.id),
        message: 'A new message was sent in your team chat! Click here to read it and respond: https://wheapp.com/',
        groupName: group.name,
        title: 'New We Hate Exercise Message',
        userId: cookies.profile.id,
        communicationMode: CommunicationMode.TEAM_CHAT,
      });
      setShowGroupMessage(false);
      updateSummary(null);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };
  return (
    <Grid container flexDirection={'row'}>
      <Fragment>
        <IconButton aria-label="more" role={'button'} onClick={(e) => setAnchorEl(e.currentTarget)} size={'small'}>
          <MoreVert></MoreVert>
        </IconButton>
        <Menu open={moreMenuOpen} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
          <MenuItem id={'SetPledgeAmount'} key={'pledge'} onClick={handleSetPledge}>
            Set Pledge Amount
          </MenuItem>
          <MenuItem
            id={'EditAccountabilityPartnerPairs'}
            key={'accountabilityPartnerPairs'}
            onClick={() => {
              setAnchorEl(null);
              setAPSettingOpen(true);
            }}
          >
            Edit Accountability Partner Pairings
          </MenuItem>
          {group.channel && (
            <MenuItem id={'SendGroupMessage'} key={'message'} onClick={handleSendMessageMenuItemClick}>
              Send Message to Group
            </MenuItem>
          )}
          <MenuItem id={'ViewReportCard'} key={'reportCard'} onClick={handleReportCard}>
            {group.finalized ? 'View Report Card' : 'View Report Card & Finalize Group'}
          </MenuItem>
          <MenuItem id={'ViewEngagementReport'} key={'engagementReport'} onClick={handleEngagementReport}>
            View Engagement Report
          </MenuItem>
        </Menu>

        <GroupSurveyConfigurationModal
          key={`${group.id}${group.name}`}
          onClose={handleCloseConfigureSurveyDialogModal}
          group={group}
          open={configureSurveyDialogOpen}
        ></GroupSurveyConfigurationModal>

        <PledgeDialog
          open={configurePledgeDialogOpen}
          groupId={group.id}
          onClose={() => {
            setConfigurePledgeDialogOpen(false);
          }}
          onSaved={() => {
            updateSummary(null);
          }}
        />

        <ReportCardDialog
          open={reportCardDialogOpen}
          onClose={() => setReportCardDialogOpen(false)}
          onSaved={() => {
            updateSummary(null);
          }}
          group={group}
        />

        <EngagementReportDialog
          open={engagementReportDialogOpen}
          onClose={() => setEngagementReportDialogOpen(false)}
          group={group}
        />

        <Dialog open={showGroupMessage} fullWidth>
          <DebugPanel displayItem={group}></DebugPanel>
          <DialogTitle>Send Group Message</DialogTitle>
          <DialogContent style={{ paddingTop: 4 }}>
            <TextField
              minRows={7}
              multiline={true}
              fullWidth
              onChange={(e: any) => setGroupMessage(e.currentTarget.value)}
            >
              {groupMessage}
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowGroupMessage(false)}>Close</Button>
            <Button variant={'contained'} onClick={() => handleSendMessage()}>
              Send
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={false}>
          <DialogTitle></DialogTitle>
          <DialogContent></DialogContent>
          <DialogActions>
            <Button></Button>
          </DialogActions>
        </Dialog>
        <AccountabilityPartnerPairingEditor
          open={apSettingOpen}
          closeDialog={() => setAPSettingOpen(false)}
          groupId={group.id}
        ></AccountabilityPartnerPairingEditor>
      </Fragment>
    </Grid>
  );
};
